import {
  Check,
  Pencil,
  Plus,
  Save,
  Trash2,
  X,
} from "lucide-react";
import {
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Textarea } from "../../../components/ui/textarea";
import { useIsMobileLayout } from "../../../hooks/useMediaQuery";
import {
  localizedFieldProps,
  extractNumericId,
  filterTermsForQuotationType,
  createAdditionalTermEntry,
  normalizeAdditionalTermEntry,
  getVisibleTerms,
} from "./quotationUtils";
import { SectionShell } from "./QuotationComponents";


function TermsAndConditionsTab({
  standardTerms = [],
  selectedStandardTerms,
  setSelectedStandardTerms,
  additionalTermEntries,
  setAdditionalTermEntries,
  previewOrder,
  setPreviewOrder,
  editable,
  onSave,
  saving,
  quotationType,
  onUpdateStandardTerm,
  flushAdditionalTermsRef,
}) {
  const [draftAdditionalTerms, setDraftAdditionalTerms] = useState(() =>
    (Array.isArray(additionalTermEntries) ? additionalTermEntries : []).map((entry, index) =>
      normalizeAdditionalTermEntry(entry, index)
    )
  );
  const [editingPreviewId, setEditingPreviewId] = useState(null);
  const [editDraft, setEditDraft] = useState({ title: "", description: "" });
  const [termUpdating, setTermUpdating] = useState(false);
  const [termEditError, setTermEditError] = useState("");
  const skipParentSyncRef = useRef(false);
  const syncingToParentRef = useRef(false);
  const draftAdditionalTermsRef = useRef(draftAdditionalTerms);
  draftAdditionalTermsRef.current = draftAdditionalTerms;

  function areAdditionalTermListsEqual(left = [], right = []) {
    if (left.length !== right.length) return false;
    return left.every((entry, index) => {
      const other = right[index];
      return (
        String(entry?.id ?? "") === String(other?.id ?? "") &&
        String(entry?.title ?? "") === String(other?.title ?? "") &&
        String(entry?.description ?? "") === String(other?.description ?? "")
      );
    });
  }

  function syncAdditionalTermsToParent(entries) {
    const next = entries.map((entry, index) => normalizeAdditionalTermEntry(entry, index));
    setAdditionalTermEntries((current) => {
      if (areAdditionalTermListsEqual(current, next)) return current;
      syncingToParentRef.current = true;
      return next;
    });
    return next;
  }

  function flushAdditionalTermsToParent() {
    return syncAdditionalTermsToParent(draftAdditionalTermsRef.current);
  }

  useEffect(() => {
    if (!flushAdditionalTermsRef) return undefined;
    flushAdditionalTermsRef.current = flushAdditionalTermsToParent;
    return () => {
      // Flush draft customs before unmount so tab switches do not drop unsaved edits.
      if (flushAdditionalTermsRef.current === flushAdditionalTermsToParent) {
        flushAdditionalTermsToParent();
        flushAdditionalTermsRef.current = null;
      }
    };
  }, [flushAdditionalTermsRef]);

  useEffect(() => {
    // Ignore echoes from our own draft → parent sync so in-progress typing is never overwritten.
    if (syncingToParentRef.current) {
      syncingToParentRef.current = false;
      return;
    }
    const next = (Array.isArray(additionalTermEntries) ? additionalTermEntries : []).map((entry, index) =>
      normalizeAdditionalTermEntry(entry, index)
    );
    setDraftAdditionalTerms((current) => {
      if (areAdditionalTermListsEqual(current, next)) return current;
      skipParentSyncRef.current = true;
      return next;
    });
  }, [additionalTermEntries]);

  const applicableStandardTerms = useMemo(() => {
    const filtered = filterTermsForQuotationType(standardTerms, quotationType);
    // Keep already-selected library terms visible even if type flags would hide them
    // (GET /terms/get/{type} is already scoped; stale isSupply/isSitc flags must not blank the preview).
    const filteredIds = new Set(filtered.map((term) => String(term.id)));
    const selectedExtras = (standardTerms || []).filter((term) => {
      const id = String(term.id);
      if (filteredIds.has(id)) return false;
      return (selectedStandardTerms || []).some((selectedId) => String(selectedId) === id);
    });
    return selectedExtras.length ? [...filtered, ...selectedExtras] : filtered;
  }, [standardTerms, quotationType, selectedStandardTerms]);

  // Preview from local draft so Header/Description typing stays responsive (no parent round-trip).
  const previewTerms = useMemo(
    () => getVisibleTerms(applicableStandardTerms, selectedStandardTerms, draftAdditionalTerms, previewOrder),
    [applicableStandardTerms, selectedStandardTerms, draftAdditionalTerms, previewOrder]
  );

  useEffect(() => {
    const ids = getVisibleTerms(applicableStandardTerms, selectedStandardTerms, draftAdditionalTerms, []).map((item) =>
      String(item.id)
    );
    setPreviewOrder((current) => {
      const currentIds = (current || []).map((id) => String(id));
      const existing = currentIds.filter((id) => ids.includes(id));
      const missing = ids.filter((id) => !existing.includes(id));
      const next = [...existing, ...missing];
      if (
        next.length === currentIds.length &&
        next.every((id, index) => id === currentIds[index])
      ) {
        return current;
      }
      return next;
    });
  }, [applicableStandardTerms, selectedStandardTerms, draftAdditionalTerms, setPreviewOrder]);

  function toggleStandardTerm(id) {
    setSelectedStandardTerms((current) => {
      const exists = current.some((item) => String(item) === String(id));
      return exists
        ? current.filter((item) => String(item) !== String(id))
        : [...current, id];
    });
  }

  function removeAdditionalTerm(index) {
    setDraftAdditionalTerms((current) => {
      const next = current.filter((_, itemIndex) => itemIndex !== index);
      // Structural changes sync immediately so Save/parent stay accurate.
      queueMicrotask(() => syncAdditionalTermsToParent(next));
      return next;
    });
  }

  function updateAdditionalTermField(index, field, value) {
    setDraftAdditionalTerms((current) =>
      current.map((entry, itemIndex) =>
        itemIndex === index ? { ...entry, [field]: value } : entry
      )
    );
  }

  function addAdditionalTerm() {
    // Keep Selected Terms Preview editing untouched — do not steal editingPreviewId.
    setDraftAdditionalTerms((current) => {
      const next = [...current, createAdditionalTermEntry(current.length)];
      queueMicrotask(() => syncAdditionalTermsToParent(next));
      return next;
    });
    setTermEditError("");
  }

  // Debounce draft → parent sync while typing so Header/Description stay responsive.
  useEffect(() => {
    if (skipParentSyncRef.current) {
      skipParentSyncRef.current = false;
      return undefined;
    }
    const timer = window.setTimeout(() => {
      syncAdditionalTermsToParent(draftAdditionalTerms);
    }, 250);
    return () => window.clearTimeout(timer);
  }, [draftAdditionalTerms]);

  function canEditPreviewTerm(term) {
    if (!editable) return false;
    // Custom terms are edited only via Additional Terms fields — no preview Edit icon.
    if (term?.source === "custom") return false;
    return (
      term?.source === "standard" &&
      typeof onUpdateStandardTerm === "function" &&
      Boolean(extractNumericId(term.libraryId ?? term.id))
    );
  }

  function startPreviewTermEdit(term) {
    if (!canEditPreviewTerm(term)) return;
    setEditingPreviewId(term.id);
    setEditDraft({
      title: String(term.title || ""),
      description: String(term.description || ""),
    });
    setTermEditError("");
  }

  function cancelPreviewTermEdit() {
    setEditingPreviewId(null);
    setEditDraft({ title: "", description: "" });
    setTermEditError("");
  }

  async function savePreviewTermEdit() {
    if (!editingPreviewId || termUpdating) return;

    const title = String(editDraft.title || "").trim();
    const description = String(editDraft.description || "").trim();
    if (!title || !description) {
      setTermEditError("Term and description are required.");
      return;
    }

    const previewTerm = previewTerms.find((term) => String(term.id) === String(editingPreviewId));
    if (!previewTerm) return;

    // Custom additional terms stay in Additional Terms — update in place from Preview.
    if (previewTerm.source === "custom") {
      setDraftAdditionalTerms((current) =>
        current.map((entry) =>
          String(entry.id) === String(editingPreviewId)
            ? { ...entry, title, description }
            : entry
        )
      );
      cancelPreviewTermEdit();
      return;
    }

    if (typeof onUpdateStandardTerm !== "function") return;

    setTermUpdating(true);
    setTermEditError("");
    try {
      await onUpdateStandardTerm(editingPreviewId, { title, description });
      cancelPreviewTermEdit();
    } catch (error) {
      setTermEditError(error?.message || "Failed to update term.");
    } finally {
      setTermUpdating(false);
    }
  }

  const isMobileLayout = useIsMobileLayout();

  return (
    <div className="space-y-3">
      <div className={`grid gap-3 ${isMobileLayout ? "" : "xl:grid-cols-[1fr_1fr]"}`}>
        <SectionShell compact title="Standard terms">
          <div className="space-y-1.5 px-3 py-3 sm:px-4">
            {applicableStandardTerms.length === 0 ? (
              <div className="rounded-[10px] border border-dashed border-[#d8e0ed] bg-[#fafcff] px-3 py-3.5 text-[11px] text-[#8fa0ba]">
                {standardTerms.length
                  ? "No standard terms are applicable for this quotation type. You can still add quotation-specific terms below."
                  : "No standard terms are currently configured. You can still add quotation-specific terms below."}
              </div>
            ) : (
              applicableStandardTerms.map((term) => {
                const selected = selectedStandardTerms.some((id) => String(id) === String(term.id));
                return (
                  <button
                    key={term.id}
                    type="button"
                    onClick={() => toggleStandardTerm(term.id)}
                    disabled={!editable}
                    className={`block w-full rounded-[12px] border px-3 py-3 text-left transition ${
                      selected
                        ? isMobileLayout
                          ? "border-[#243c93] bg-white"
                          : "border-[#dde4f2] bg-[#f2f5ff]"
                        : "border-[#edf1f7] bg-white hover:border-[#d8e0ed] hover:bg-[#fafcff]"
                    } disabled:cursor-not-allowed disabled:opacity-80`}
                  >
                    <div className="flex items-start justify-between gap-2.5">
                      <div className="min-w-0 flex-1">
                        <p className="text-[13px] font-semibold text-[#2d394c]">{term.title}</p>
                        {selected || !isMobileLayout ? (
                          <p className="mt-1 text-[12px] leading-5 text-[#6f82a1]">{term.description}</p>
                        ) : (
                          <p className="mt-1 line-clamp-2 text-[12px] leading-5 text-[#6f82a1]">{term.description}</p>
                        )}
                      </div>
                      {isMobileLayout ? (
                        <span
                          className={`relative mt-0.5 h-7 w-12 shrink-0 rounded-full transition ${
                            selected ? "bg-[#243c93]" : "bg-[#d1d9e8]"
                          }`}
                          aria-hidden
                        >
                          <span
                            className={`absolute top-0.5 h-6 w-6 rounded-full bg-white transition ${
                              selected ? "left-[22px]" : "left-0.5"
                            }`}
                          />
                        </span>
                      ) : (
                        <div className={`mt-0.5 grid h-4 w-4 place-items-center rounded-md border ${selected ? "border-[#2944a8] bg-[#2944a8] text-white" : "border-[#c5cfde] bg-white text-transparent"}`}>
                          <Check className="h-3 w-3" />
                        </div>
                      )}
                    </div>
                  </button>
                );
              })
            )}
          </div>
        </SectionShell>

        <SectionShell compact title="Selected terms preview">
          <div className="px-3 py-3 sm:px-4">
            {previewTerms.length === 0 ? (
              <div className="py-4 text-[11px] text-[#8fa0ba]">Select standard terms or add quotation-specific terms to build the final PDF order.</div>
            ) : (
              previewTerms.map((term, index) => {
                const isEditing = String(editingPreviewId) === String(term.id);
                const showEdit = canEditPreviewTerm(term);
                return (
                  <div key={term.id} className={`flex items-start gap-2.5 py-2.5 ${index < previewTerms.length - 1 ? "border-b border-[#edf1f7]" : ""}`}>
                    <div className="flex-1">
                      {isEditing ? (
                        <div className="space-y-2">
                          <div className="flex items-center justify-between gap-2">
                            <p className="text-[11px] font-semibold uppercase tracking-[0.04em] text-[#8ea0bd]">
                              {index + 1}. Edit term
                            </p>
                            <div className="flex items-center gap-1.5">
                              <button
                                type="button"
                                onClick={cancelPreviewTermEdit}
                                disabled={termUpdating}
                                className="grid h-7 w-7 place-items-center rounded-lg border border-[#dde4f2] text-[#7c8faa] transition hover:bg-[#f7faff] disabled:opacity-40"
                                aria-label="Cancel edit"
                              >
                                <X className="h-3.5 w-3.5" />
                              </button>
                              <button
                                type="button"
                                onClick={savePreviewTermEdit}
                                disabled={
                                  termUpdating ||
                                  !String(editDraft.title || "").trim() ||
                                  !String(editDraft.description || "").trim()
                                }
                                className="inline-flex h-7 items-center gap-1 rounded-lg border border-[#2944a8] bg-[#2944a8] px-2.5 text-[11px] font-semibold text-white transition hover:bg-[#1d327f] disabled:cursor-not-allowed disabled:opacity-50"
                              >
                                <Save className="h-3.5 w-3.5" />
                                {termUpdating ? "Saving..." : "Save"}
                              </button>
                            </div>
                          </div>
                          <input
                            value={editDraft.title}
                            onChange={(event) =>
                              setEditDraft((current) => ({ ...current, title: event.target.value }))
                            }
                            disabled={termUpdating}
                            className="w-full rounded-[10px] border border-[#cdd8ea] bg-white px-2.5 py-1.5 text-[12px] font-semibold text-[#2d394c] outline-hidden transition focus:border-[#9fb4dd]"
                            placeholder="Term"
                            {...localizedFieldProps()}
                          />
                          <Textarea
                            value={editDraft.description}
                            onChange={(event) =>
                              setEditDraft((current) => ({ ...current, description: event.target.value }))
                            }
                            disabled={termUpdating}
                            className="min-h-[72px] rounded-[10px] border-[#cdd8ea] px-3 py-2 text-[12px] leading-5 text-[#2d394c] placeholder:text-[#8e9cb2]"
                            placeholder="Description"
                            {...localizedFieldProps()}
                          />
                          {termEditError ? (
                            <p className="text-[11px] font-medium text-[#c2410c]">{termEditError}</p>
                          ) : null}
                        </div>
                      ) : (
                        <>
                          <div className="flex items-center justify-between gap-2">
                            <p className="text-[12px] font-semibold text-[#2d394c]">{index + 1}. {term.title}</p>
                            {showEdit ? (
                              <button
                                type="button"
                                onClick={() => startPreviewTermEdit(term)}
                                disabled={termUpdating}
                                className="grid h-7 w-7 place-items-center rounded-lg border border-[#dde4f2] text-[#7c8faa] transition hover:bg-[#f7faff] hover:text-[#2944a8] disabled:opacity-40"
                                aria-label="Edit term"
                                title="Edit"
                              >
                                <Pencil className="h-3.5 w-3.5" />
                              </button>
                            ) : null}
                          </div>
                          <p className="mt-0.5 text-[11px] leading-5 text-[#6f82a1]">{term.description}</p>
                        </>
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </SectionShell>
      </div>

      <SectionShell compact title="Additional terms">
        <div className="px-3 pb-3 pt-2.5 sm:px-4">
          <div className="space-y-2">
            {draftAdditionalTerms.length === 0 ? (
              <div className="rounded-[10px] border border-dashed border-[#d8e0ed] bg-[#fafcff] px-3 py-3.5 text-[11px] text-[#8fa0ba]">
                Add custom quotation-specific terms here. Selected standard terms stay editable in Selected terms preview.
              </div>
            ) : (
              draftAdditionalTerms.map((term, index) => (
                <div
                  key={term.id || `additional-term-${index}`}
                  className="rounded-[10px] border border-[#edf1f7] bg-white px-3 py-2.5"
                >
                  <div className="mb-2 flex items-center justify-between gap-2">
                    <p className="text-[11px] font-semibold uppercase tracking-[0.04em] text-[#8ea0bd]">
                      Term {index + 1}
                    </p>
                    {editable ? (
                      <button
                        type="button"
                        onClick={() => removeAdditionalTerm(index)}
                        className="grid h-7 w-7 shrink-0 place-items-center rounded-lg border border-[#dde4f2] text-[#7c8faa] transition hover:bg-[#f7faff] hover:text-[#2944a8]"
                        aria-label="Remove additional term"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    ) : null}
                  </div>
                  {editable ? (
                    <div className="space-y-2">
                      <input
                        value={term.title ?? ""}
                        onChange={(event) => updateAdditionalTermField(index, "title", event.target.value)}
                        onBlur={flushAdditionalTermsToParent}
                        className="w-full rounded-[10px] border border-[#cdd8ea] bg-white px-2.5 py-1.5 text-[12px] font-semibold text-[#2d394c] outline-hidden transition focus:border-[#9fb4dd]"
                        placeholder="Term Header"
                        {...localizedFieldProps()}
                      />
                      <Textarea
                        value={term.description ?? ""}
                        onChange={(event) => updateAdditionalTermField(index, "description", event.target.value)}
                        onBlur={flushAdditionalTermsToParent}
                        className="min-h-[72px] rounded-[10px] border-[#cdd8ea] px-3 py-2 text-[12px] leading-5 text-[#2d394c] placeholder:text-[#8e9cb2]"
                        placeholder="Description"
                        {...localizedFieldProps()}
                      />
                    </div>
                  ) : (
                    <div>
                      <p className="text-[12px] font-semibold text-[#2d394c]">
                        {String(term.title || "").trim() || "—"}
                      </p>
                      <p className="mt-0.5 text-[11px] leading-5 text-[#6f82a1]">
                        {String(term.description || "").trim() || "—"}
                      </p>
                    </div>
                  )}
                </div>
              ))
            )}
            {editable && (
              <button
                type="button"
                onClick={addAdditionalTerm}
                className="inline-flex items-center gap-1.5 rounded-lg px-1.5 py-1.5 text-[12px] font-semibold text-[#2944a8] transition hover:bg-[#f7faff] hover:text-[#1d327f]"
              >
                <Plus className="h-3.5 w-3.5" />
                <span>Add term</span>
              </button>
            )}
          </div>
        </div>
      </SectionShell>
    </div>
  );
}

export {
  TermsAndConditionsTab
};
